import {
  isChatStreamPendingResumeError,
  startChatStreamResume,
} from '@terra/shared-utils';
import { type RefObject, useCallback, useEffect, useRef } from 'react';
import {
  type ChatInstanceStatus,
  isRecoverableStreamError,
} from '../utils/chatNetworkErrors';

const STREAM_RECOVERY_DEBOUNCE_MS = 2_000;
const STREAM_PENDING_REPOLL_MS = 15_000;
const STREAM_UNREACHABLE_REPOLL_MS = 10_000;

type UseStreamRecoveryParams = {
  error: Error | undefined;
  status: ChatInstanceStatus;
  resumeStream: () => void;
  intentionalAbortRef: RefObject<boolean>;
};

export function useStreamRecovery({
  error,
  status,
  resumeStream,
  intentionalAbortRef,
}: UseStreamRecoveryParams) {
  const lastRecoveryAtRef = useRef(0);
  const recoveryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorRef = useRef(error);
  const statusRef = useRef(status);
  const resumeStreamRef = useRef(resumeStream);

  errorRef.current = error;
  statusRef.current = status;
  resumeStreamRef.current = resumeStream;

  const cancelScheduledRecovery = useCallback(() => {
    if (recoveryTimeoutRef.current !== null) {
      clearTimeout(recoveryTimeoutRef.current);
      recoveryTimeoutRef.current = null;
    }
  }, []);

  const scheduleRecovery = useCallback(() => {
    if (intentionalAbortRef.current) {
      return;
    }

    const currentError = errorRef.current;

    if (!currentError || !isRecoverableStreamError(currentError)) {
      return;
    }

    if (statusRef.current !== 'error') {
      return;
    }

    if (recoveryTimeoutRef.current !== null) {
      return;
    }

    const now = Date.now();
    const delayMs = Math.max(
      0,
      STREAM_RECOVERY_DEBOUNCE_MS - (now - lastRecoveryAtRef.current),
    );

    recoveryTimeoutRef.current = setTimeout(() => {
      recoveryTimeoutRef.current = null;
      if (intentionalAbortRef.current) {
        return;
      }

      const latestError = errorRef.current;

      if (!latestError || !isRecoverableStreamError(latestError)) {
        return;
      }

      if (statusRef.current !== 'error') {
        return;
      }

      lastRecoveryAtRef.current = Date.now();
      startChatStreamResume({ resumeStream: resumeStreamRef.current });
    }, delayMs);
  }, [intentionalAbortRef]);

  useEffect(() => {
    if (status === 'submitted' || status === 'streaming') {
      cancelScheduledRecovery();
    }
  }, [cancelScheduledRecovery, status]);

  useEffect(() => {
    if (
      status !== 'error' ||
      !isRecoverableStreamError(error) ||
      intentionalAbortRef.current
    ) {
      return;
    }

    const repoll = setInterval(
      scheduleRecovery,
      isChatStreamPendingResumeError(error)
        ? STREAM_PENDING_REPOLL_MS
        : STREAM_UNREACHABLE_REPOLL_MS,
    );

    return () => clearInterval(repoll);
  }, [error, intentionalAbortRef, scheduleRecovery, status]);

  useEffect(() => {
    if (!error) {
      return;
    }

    if (intentionalAbortRef.current) {
      intentionalAbortRef.current = false;

      return;
    }

    scheduleRecovery();

    return cancelScheduledRecovery;
  }, [cancelScheduledRecovery, error, intentionalAbortRef, scheduleRecovery]);

  useEffect(() => {
    const handleRecoveryTrigger = () => {
      scheduleRecovery();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleRecoveryTrigger();
      }
    };

    window.addEventListener('online', handleRecoveryTrigger);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const cleanupSystem = window.electronAPI?.system?.onSystemResumed(
      handleRecoveryTrigger,
    );

    return () => {
      cancelScheduledRecovery();
      window.removeEventListener('online', handleRecoveryTrigger);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanupSystem?.();
    };
  }, [cancelScheduledRecovery, scheduleRecovery]);

  return { cancelScheduledRecovery };
}
